import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  type Unsubscribe,
} from 'firebase/firestore';
import type { BackendTask } from '@modules/background-tasks';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ContentPost } from '@modules/content-plan/types';
import { contentPostToPostLike, type PostLike } from '@modules/content-plan/utils/postLike';
import {
  legacyDraftRunIdForTask,
  publicationPathForTaskPostArtifactFallback,
  normalizeTaskPostArtifactRefs,
  type TaskPostArtifactRef,
} from '@modules/content-plan/utils/taskPostArtifacts';

export interface ResolvedTaskPostArtifact {
  ref: TaskPostArtifactRef;
  post: PostLike | null;
  missing?: boolean;
}

export interface UseTaskPostArtifactsReturn {
  supported: boolean;
  artifacts: ResolvedTaskPostArtifact[];
  loading: boolean;
  error: Error | null;
}

function pathSegments(path: string): string[] {
  return path.split('/').map((segment) => segment.trim()).filter(Boolean);
}

function draftDataToPost(raw: Record<string, unknown>): ContentPost {
  return ((raw.data && typeof raw.data === 'object') ? raw.data : raw) as ContentPost;
}

function draftRefFromDoc(task: BackendTask, projectId: string, runId: string, docId: string, raw: Record<string, unknown>): TaskPostArtifactRef {
  const post = draftDataToPost(raw) as unknown as Record<string, unknown>;
  const topicId = typeof raw.topicId === 'string'
    ? raw.topicId
    : typeof post.topicId === 'string'
      ? post.topicId
      : undefined;
  const platform = typeof raw.platform === 'string'
    ? raw.platform
    : typeof post.platform === 'string'
      ? post.platform
      : undefined;
  return {
    artifactType: 'content_post',
    postId: docId,
    projectId,
    topicId,
    platform,
    format: typeof post.format === 'string' ? post.format : undefined,
    storage: 'post_generation_draft',
    firestorePath: `projects/${projectId}/post_generations/${runId}/generatedPosts/${docId}`,
    editableUrl: task.targetEntityPath?.frontendUrl ?? (
      topicId
        ? `/project/${projectId}/content-plan?cpTab=backlog&topic=${encodeURIComponent(topicId)}`
        : `/project/${projectId}/content-plan`
    ),
    previewText: [post.title, post.hook, post.body].find((v) => typeof v === 'string' && v.trim()) as string | undefined,
    createdByTaskId: task.id,
    runId,
  };
}

function publicationLinkedPostRef(
  task: BackendTask,
  projectId: string,
  rawPublication: Record<string, unknown>,
): TaskPostArtifactRef | null {
  const postId = typeof rawPublication.contentPostId === 'string' && rawPublication.contentPostId.trim()
    ? rawPublication.contentPostId.trim()
    : null;
  if (!postId) return null;
  const topicId = typeof rawPublication.topicId === 'string' ? rawPublication.topicId : undefined;
  const platform = typeof rawPublication.platform === 'string' ? rawPublication.platform : undefined;
  return {
    artifactType: 'content_post',
    postId,
    projectId,
    topicId,
    platform,
    storage: 'content_posts',
    firestorePath: `projects/${projectId}/content_posts/${postId}`,
    editableUrl: task.targetEntityPath?.frontendUrl ?? (
      topicId
        ? `/project/${projectId}/content-plan?cpTab=backlog&topic=${encodeURIComponent(topicId)}`
        : `/project/${projectId}/content-plan`
    ),
    createdByTaskId: task.id,
  };
}

export function useTaskPostArtifacts(task: BackendTask | null | undefined): UseTaskPostArtifactsReturn {
  const supported = String(task?.taskType ?? '') === 'content-post-generation';
  const refs = useMemo(() => normalizeTaskPostArtifactRefs(task), [task]);
  const publicationFallbackPath = useMemo(() => publicationPathForTaskPostArtifactFallback(task), [task]);
  const legacyRunId = useMemo(() => legacyDraftRunIdForTask(task), [task]);
  const [artifacts, setArtifacts] = useState<ResolvedTaskPostArtifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!task || !supported) {
      setArtifacts([]);
      setLoading(false);
      setError(null);
      return undefined;
    }
    const projectId = (task as unknown as { projectId?: string }).projectId;
    if (!projectId) {
      setArtifacts([]);
      setLoading(false);
      setError(new Error('Task is missing projectId'));
      return undefined;
    }
    const db = CoreFirebaseService.getInstance().firestore;
    const unsubscribers: Unsubscribe[] = [];
    setLoading(true);
    setError(null);

    if (refs.length > 0) {
      const pending = new Set(refs.map((ref) => ref.postId));
      const next = new Map<string, ResolvedTaskPostArtifact>();
      const flush = () => {
        setArtifacts(refs.map((ref) => next.get(ref.postId) ?? { ref, post: null }));
        setLoading(pending.size > 0);
      };

      refs.forEach((ref) => {
        const segments = pathSegments(ref.firestorePath);
        if (segments.length % 2 !== 0) {
          next.set(ref.postId, { ref, post: null, missing: true });
          pending.delete(ref.postId);
          flush();
          return;
        }
        const unsubscribe = onSnapshot(
          doc(db, ...(segments as [string, ...string[]])),
          (snap) => {
            pending.delete(ref.postId);
            if (!snap.exists()) {
              next.set(ref.postId, { ref, post: null, missing: true });
              flush();
              return;
            }
            const raw = snap.data() as Record<string, unknown>;
            const post = ref.storage === 'post_generation_draft'
              ? draftDataToPost(raw)
              : ({ id: snap.id, ...raw } as ContentPost);
            next.set(ref.postId, {
              ref,
              post: contentPostToPostLike(post, {
                kind: ref.storage === 'post_generation_draft' ? 'content_post_draft' : 'content_post',
                projectId: ref.projectId,
                runId: ref.runId,
              }),
            });
            flush();
          },
          (err) => {
            pending.delete(ref.postId);
            setError(err as Error);
            flush();
          },
        );
        unsubscribers.push(unsubscribe);
      });

      flush();
      return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    }

    if (publicationFallbackPath) {
      const segments = pathSegments(publicationFallbackPath);
      if (segments.length % 2 !== 0) {
        setArtifacts([]);
        setLoading(false);
        setError(new Error(`Invalid publication path: ${publicationFallbackPath}`));
        return undefined;
      }
      let postUnsubscribe: Unsubscribe | undefined;
      const unsubscribe = onSnapshot(
        doc(db, ...(segments as [string, ...string[]])),
        (publicationSnap) => {
          postUnsubscribe?.();
          postUnsubscribe = undefined;
          if (!publicationSnap.exists()) {
            setArtifacts([]);
            setLoading(false);
            return;
          }
          const ref = publicationLinkedPostRef(
            task,
            projectId,
            publicationSnap.data() as Record<string, unknown>,
          );
          if (!ref) {
            setArtifacts([]);
            setLoading(false);
            return;
          }
          setLoading(true);
          const postSegments = pathSegments(ref.firestorePath);
          postUnsubscribe = onSnapshot(
            doc(db, ...(postSegments as [string, ...string[]])),
            (postSnap) => {
              if (!postSnap.exists()) {
                setArtifacts([{ ref, post: null, missing: true }]);
                setLoading(false);
                return;
              }
              const raw = postSnap.data() as Record<string, unknown>;
              const post = { id: postSnap.id, ...raw } as ContentPost;
              setArtifacts([{
                ref,
                post: contentPostToPostLike(post, {
                  kind: 'content_post',
                  projectId: ref.projectId,
                }),
              }]);
              setLoading(false);
            },
            (err) => {
              setError(err as Error);
              setArtifacts([{ ref, post: null }]);
              setLoading(false);
            },
          );
        },
        (err) => {
          setError(err as Error);
          setLoading(false);
        },
      );
      return () => {
        postUnsubscribe?.();
        unsubscribe();
      };
    }

    if (legacyRunId) {
      const draftsRef = collection(db, 'projects', projectId, 'post_generations', legacyRunId, 'generatedPosts');
      const unsubscribe = onSnapshot(
        query(draftsRef, orderBy('createdAt', 'asc')),
        (snap) => {
          const list = snap.docs.map((draftDoc) => {
            const raw = draftDoc.data() as Record<string, unknown>;
            const ref = draftRefFromDoc(task, projectId, legacyRunId, draftDoc.id, raw);
            return {
              ref,
              post: contentPostToPostLike(draftDataToPost(raw), {
                kind: 'content_post_draft',
                projectId,
                runId: legacyRunId,
              }),
            };
          });
          setArtifacts(list);
          setLoading(false);
        },
        (err) => {
          setError(err as Error);
          setLoading(false);
        },
      );
      return () => unsubscribe();
    }

    setArtifacts([]);
    setLoading(false);
    return undefined;
  }, [task, supported, refs, publicationFallbackPath, legacyRunId]);

  return { supported, artifacts, loading, error };
}
